import { useMutation } from "@apollo/client";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  SetSubmissionDueDatesMutation,
  SetSubmissionDueDatesMutationVariables,
} from "generated-graphql/graphql";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { SET_SUBMISSION_DUE_DATES_MUTATION } from "queries/submissions";
import { Row } from "./columns";
import { partitionSubmissions } from "./utils";

type ChangeDueDateDialogProps = {
  open: boolean;
  onClose: () => void;
  submissions: Row[];
  onSuccess: () => void;
};

export function ChangeDueDateDialog({
  open,
  onClose,
  submissions,
  onSuccess,
}: ChangeDueDateDialogProps) {
  const [selectedDate, setSelectedDate] = useState<DateTime | null>(null);

  const alerts = useAlerts();

  const [setDueDates, { loading }] = useMutation<
    SetSubmissionDueDatesMutation,
    SetSubmissionDueDatesMutationVariables
  >(SET_SUBMISSION_DUE_DATES_MUTATION, {
    refetchQueries: ["WasteSubmissionsForStaff", "WasteSubmissionsStaffSummary"],
    awaitRefetchQueries: true,
  });

  const handleClose = () => {
    setSelectedDate(null);
    onClose();
  };

  const {
    realSubmissions: materializedSubmissions,
    virtualEvents,
    uniqueSubmissionIds,
    virtualEventIds,
  } = useMemo(() => partitionSubmissions(submissions), [submissions]);

  const handleSubmit = async () => {
    if (!selectedDate) {
      alerts.error("Please select a due date");
      return;
    }

    const dueDateIso = selectedDate.toISO();
    if (!dueDateIso) {
      alerts.error("Invalid date selected");
      return;
    }

    if (uniqueSubmissionIds.length === 0 && virtualEventIds.length === 0) {
      alerts.error("No submissions selected");
      return;
    }

    try {
      const result = await setDueDates({
        variables: {
          submissionIds: uniqueSubmissionIds,
          virtualEventIds: virtualEventIds,
          dueDate: dueDateIso,
        },
      });

      if (result.data?.setSubmissionDueDates.succeeded) {
        const totalUpdated = result.data.setSubmissionDueDates.updated ?? 0;
        const virtualCount = virtualEventIds.length;
        alerts.success(
          `Successfully updated due date for ${totalUpdated} submission(s)` +
            (virtualCount > 0
              ? ` (${virtualCount} virtual event(s) were created)`
              : "")
        );
        handleClose();
        onSuccess();
      } else {
        alerts.error("Failed to update due dates");
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      alerts.error(`Failed to update due dates: ${message}`);
    }
  };

  const tableRows = useMemo(() => {
    const materializedRows = materializedSubmissions.map((s) => ({
      id: s.id,
      isVirtual: false,
      currentDueDate: s.deadline,
      facility:
        s.facilities?.map((f: { name: string }) => f.name).join(", ") || "",
      submissionType: s.submissionType,
      title: s.title,
    }));

    const virtualRows = virtualEvents.map((s) => ({
      id: s.id,
      isVirtual: true,
      currentDueDate: s.deadline,
      facility:
        s.facilities?.map((f: { name: string }) => f.name).join(", ") || "",
      submissionType: s.submissionType,
      title: s.title,
    }));

    return [...materializedRows, ...virtualRows];
  }, [materializedSubmissions, virtualEvents]);

  const columns: CustomGridColDef<(typeof tableRows)[number]>[] = [
    {
      field: "currentDueDate",
      headerName: "Current Due Date",
      width: 130,
      valueGetter: (value, row) =>
        row.currentDueDate
          ? DateTime.fromISO(row.currentDueDate).toFormat("MM/dd/yyyy")
          : "",
    },
    {
      field: "facility",
      headerName: "Facility",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "submissionType",
      headerName: "Submission Type",
      width: 130,
      valueGetter: (value, row) => prettyPrintEnumValue(row.submissionType),
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "newDueDate",
      headerName: "New Due Date",
      width: 130,
      valueGetter: () => {
        return selectedDate ? selectedDate.toFormat("MM/dd/yyyy") : "";
      },
    },
    {
      field: "action",
      headerName: "Action",
      width: 100,
      valueGetter: (value, row) => {
        return row.isVirtual ? "Create" : "Update";
      },
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            width: 900,
            maxWidth: "90vw",
          },
        },
      }}
    >
      <DialogTitle>
        Change Due Date for {submissions.length} Submission(s)
        {virtualEvents.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {virtualEvents.length} virtual event(s) will be created as new
            submissions
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to update the due date of the selected{" "}
            {submissions.length} submission(s)?
          </Typography>

          <Box>
            <DatePicker
              label="New Due Date *"
              value={selectedDate}
              onChange={(newValue) => setSelectedDate(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />
          </Box>

          {selectedDate && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Submissions ({materializedSubmissions.length} will be updated
                {virtualEvents.length > 0 &&
                  `, ${virtualEvents.length} will be created`}
                )
              </Typography>
              <DataGrid
                rows={tableRows}
                columns={columns}
                disableRowSelectionOnClick
                pagination
                paginationMode="client"
                pageSizeOptions={[5, 10]}
                hideFooterPagination={false}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 10 },
                  },
                }}
                sx={{ maxHeight: 600 }}
              />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !selectedDate}
        >
          {loading ? "Updating..." : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
